<template>
  <div data-testid="onboarding-brokerage-license">
    <v-divider class="mb-6" />

    <h3 class="text-subtitle-1 font-weight-bold mb-1">
      {{ $t('onboarding.wizard.data-entry.brokerage-license') }}
    </h3>
    <p class="text-body-2 text-medium-emphasis mb-4">
      {{ $t('onboarding.wizard.data-entry.brokerage-license-hint') }}
    </p>

    <OnboardingFormField
      class="mb-4"
      field-key="state"
      icon="mdi-map"
      :label="$t('onboarding.wizard.data-entry.state')"
    >
      <v-autocomplete
        :model-value="resolveUsStateCode(formData.state)"
        id="onboarding-field-state"
        data-ai-field="state"
        :items="US_STATE_ITEMS"
        item-title="title"
        item-value="value"
        :class="fieldClass('state')"
        variant="outlined"
        density="comfortable"
        rounded="lg"
        clearable
        auto-select-first
        hide-details
        :placeholder="$t('onboarding.wizard.data-entry.state-placeholder')"
        @focus="onFieldFocus('state')"
        @update:model-value="updateField('state', $event ?? '')"
      />
    </OnboardingFormField>

    <OnboardingFormField
      field-key="brokerageName"
      icon="mdi-domain"
      :label="$t('onboarding.wizard.data-entry.us-brokerage-name')"
      :hint="$t('onboarding.wizard.data-entry.us-brokerage-name-hint')"
    >
      <v-text-field
        :model-value="formData.brokerageName"
        id="onboarding-field-brokerageName"
        data-ai-field="brokerageName"
        :class="fieldClass('brokerageName')"
        variant="outlined"
        density="comfortable"
        rounded="lg"
        :readonly="sameAsName"
        :bg-color="sameAsName ? 'grey-lighten-4' : undefined"
        :placeholder="$t('onboarding.wizard.data-entry.us-brokerage-name-placeholder')"
        hide-details
        @focus="onFieldFocus('brokerageName')"
        @update:model-value="updateField('brokerageName', $event)"
      />
    </OnboardingFormField>

    <v-checkbox
      :model-value="sameAsName"
      class="mb-2"
      color="primary"
      density="comfortable"
      hide-details
      data-testid="brokerage-same-as-name"
      @update:model-value="updateField('brokerageNameSameAsName', Boolean($event))"
    >
      <template #label>
        <span class="text-body-2">
          {{ $t('onboarding.wizard.data-entry.brokerage-same-as-name') }}
        </span>
      </template>
    </v-checkbox>

    <OnboardingFormField
      class="mb-4"
      field-key="licenseNumber"
      icon="mdi-card-account-details"
      :label="$t('onboarding.wizard.data-entry.us-license-number')"
    >
      <v-text-field
        :model-value="formData.licenseNumber"
        id="onboarding-field-licenseNumber"
        data-ai-field="licenseNumber"
        :class="fieldClass('licenseNumber')"
        variant="outlined"
        density="comfortable"
        rounded="lg"
        :placeholder="$t('onboarding.wizard.data-entry.us-license-number-placeholder')"
        hide-details
        @focus="onFieldFocus('licenseNumber')"
        @update:model-value="updateField('licenseNumber', $event)"
      />
    </OnboardingFormField>

    <OnboardingFormField
      class="mb-4"
      field-key="licenseStatus"
      icon="mdi-certificate-outline"
      :label="$t('onboarding.wizard.data-entry.license-status')"
      :hint="$t('onboarding.wizard.data-entry.license-status-hint')"
    >
      <v-text-field
        :model-value="formData.licenseStatus"
        id="onboarding-field-licenseStatus"
        data-ai-field="licenseStatus"
        :class="fieldClass('licenseStatus')"
        variant="outlined"
        density="comfortable"
        rounded="lg"
        :placeholder="$t('onboarding.wizard.data-entry.license-status-placeholder')"
        hide-details
        @focus="onFieldFocus('licenseStatus')"
        @update:model-value="updateField('licenseStatus', $event)"
      />
    </OnboardingFormField>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useOnboardingDataEntryContext } from '../../composables/onboardingDataEntryContext'
import { resolveUsStateCode, US_STATE_ITEMS } from '../../lib/usStates'
import OnboardingFormField from './OnboardingFormField.vue'

/**
 * US only: State, brokerage name, license number and license status. Shown in
 * generated materials' contact section. "I work independently" mirrors the
 * company (licensed) name into the brokerage name and locks the field; the
 * syncing itself lives in the wizard (`useBrokerageSameAsName`).
 */
const { formData, updateField, fieldClass, onFieldFocus } = useOnboardingDataEntryContext()

const sameAsName = computed(() => Boolean(formData.value.brokerageNameSameAsName))
</script>
